// src/pages/NoteList.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, InputAdornment, Paper, Stack, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Pagination, Skeleton, Link as MLink, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import SearchRounded from '@mui/icons-material/SearchRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import AlarmRounded from '@mui/icons-material/AlarmRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import WorkRounded from '@mui/icons-material/WorkRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import LightbulbRounded from '@mui/icons-material/LightbulbRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import StickyNote2Rounded from '@mui/icons-material/StickyNote2Rounded';
import Swal from 'sweetalert2';
import { Checkbox } from '@mui/material';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkSelectBar from '../components/ui/BulkSelectBar';
import { deleteNote, fetchNotes } from '../services/api';
import { NotesCategoryTabs, useNoteFacets } from '../components/notes/NotesFilterPanel';
import NoteFormDialog from '../components/notes/NoteFormDialog';
import PageHeader from '../components/ui/PageHeader';
import ViewToggle from '../components/ui/ViewToggle';
import EmptyState from '../components/ui/EmptyState';
import '../styles/noteContent.css';

/* -------------------- Types -------------------- */
interface NoteFile { _id: string; url: string; filename?: string; size?: number; mimetype?: string; }
type IdOrObj = string | { _id: string; name?: string } | null | undefined;
interface Note {
  _id: string;
  slug?: string;
  title?: string;
  project?: IdOrObj;
  entryDate?: string | Date | null;
  note?: any;
  category?: string | null;
  tags?: string[];
  images?: NoteFile[];
  updatedAt?: string;
}

/* -------------------- Utils -------------------- */
const API_BASE = (process.env.REACT_APP_BASE_URL || '').replace(/\/+$/, '');
const API_ORIGIN = API_BASE.replace(/\/api$/i, '');
const normalizeUrl = (u?: string) => {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/')) return `${API_ORIGIN}${u}`;
  return `${API_ORIGIN}/${u}`;
};

const nameOf = (v: IdOrObj): string => (typeof v === 'object' && v?.name) ? v.name : '';
const hasText = (v?: string | number | null): boolean => v !== null && v !== undefined && String(v).trim() !== '';
const fmtDate = (v?: string | Date | null): string => {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const htmlToText = (input?: string | null): string => {
  if (!input) return '';
  const div = document.createElement('div');
  div.innerHTML = String(input);
  return (div.textContent || div.innerText || '').replace(/ /g, ' ').replace(/\s+/g, ' ').trim();
};

const sanitizeHtml = (dirty: string): string => {
  const template = document.createElement('template');
  template.innerHTML = dirty;
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_ELEMENT);
  const toRemove: Element[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) {
    const el = n as Element;
    const tag = el.tagName.toLowerCase();
    if (['script', 'style', 'iframe', 'object', 'embed'].includes(tag)) { toRemove.push(el); continue; }
    Array.from(el.attributes).forEach((attr) => {
      if (attr.name.toLowerCase().startsWith('on')) el.removeAttribute(attr.name);
      if ((attr.name === 'href' || attr.name === 'src') && /^\s*(javascript|data):/i.test(attr.value)) {
        el.setAttribute(attr.name, '#');
      }
    });
  }
  toRemove.forEach((el) => el.remove());
  return template.innerHTML;
};

const CATEGORY_COLORS: Record<string, string> = {
  Personal: '#0277bd', Work: '#6226A7', Ideas: '#F6A938', Meeting: '#CF1E52', Others: '#546E7A',
};
const categoryAccent = (category?: string | null) => CATEGORY_COLORS[category || ''] || '#546E7A';
const categoryIcon = (category?: string | null) => {
  switch (category) {
    case 'Work': return WorkRounded;
    case 'Personal': return PersonRounded;
    case 'Ideas': return LightbulbRounded;
    case 'Meeting': return GroupsRounded;
    default: return StickyNote2Rounded;
  }
};

const extOf = (name?: string) => (name || '').split('.').pop()?.toLowerCase() || '';
const isImage = (f: NoteFile) => (f.mimetype?.startsWith('image/')) || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extOf(f.filename));

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 30, 50, 100];
const LS_VIEW_MODE_KEY = 'quillstack.notes.cardLayout';

const NoteList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [cardLayout, setCardLayout] = useState<'list' | 'grid'>(() => {
    try { return (localStorage.getItem(LS_VIEW_MODE_KEY) as 'list' | 'grid') || 'grid'; } catch { return 'grid'; }
  });
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [facetRefreshKey, setFacetRefreshKey] = useState(0);
  const { categories } = useNoteFacets(facetRefreshKey);

  const [viewNote, setViewNote] = useState<Note | null>(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNoteSlug, setEditingNoteSlug] = useState<string | null>(null);

  // The page is mounted once by the /notes/* route; the dialog follows the URL.
  useEffect(() => {
    const path = location.pathname.replace(/\/+$/, '');
    const edit = path.match(/^\/notes\/edit\/([^/]+)$/);
    if (path === '/notes/create') {
      setEditingNoteSlug(null);
      setNoteDialogOpen(true);
    } else if (edit) {
      setEditingNoteSlug(decodeURIComponent(edit[1]));
      setNoteDialogOpen(true);
    } else {
      setNoteDialogOpen(false);
    }
  }, [location.pathname]);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchNotes(search, '');
      setNotes(Array.isArray(res.data) ? res.data : res.data?.items || []);
      setFacetRefreshKey((k) => k + 1);
    } catch {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { loadNotes(); }, [loadNotes]);
  useEffect(() => { setPage(1); }, [selectedCategory, search]);

  const handleSetCardLayout = (mode: 'list' | 'grid') => {
    setCardLayout(mode);
    try { localStorage.setItem(LS_VIEW_MODE_KEY, mode); } catch { /* ignore */ }
  };

  const filtered = useMemo(() => notes.filter((n) => {
    if (selectedCategory && n.category !== selectedCategory) return false;
    return true;
  }), [notes, selectedCategory]);

  const sorted = useMemo(
    () => [...filtered].sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()),
    [filtered]
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  const bulk = useBulkSelect();
  const noteKey = (n: Note) => n.slug || n._id;

  const handleDelete = async (n: Note) => {
    const result = await Swal.fire({
      title: 'Delete note?', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;
    await deleteNote(n.slug || n._id);
    loadNotes();
  };

  const bodyExcerpt = (n: Note) => {
    const text = htmlToText(n.note);
    return text.length > 140 ? `${text.slice(0, 140)}…` : text;
  };

  const renderCard = (n: Note, index = 0) => {
    const CategoryIcon = categoryIcon(n.category);
    const accent = categoryAccent(n.category);
    const grid = cardLayout === 'grid';
    const excerpt = bodyExcerpt(n);
    const open = () => setViewNote(n);

    const iconTile = (size: number) => (
      <Box
        sx={{
          width: size, height: size, flexShrink: 0, display: 'grid', placeItems: 'center',
          borderRadius: '16px', color: '#fff',
          background: 'linear-gradient(135deg, rgba(255,255,255,.42), rgba(255,255,255,.12))',
          border: '1px solid rgba(255,255,255,.5)', backdropFilter: 'blur(6px)',
        }}
      >
        <CategoryIcon />
      </Box>
    );

    const actions = (
      <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
        <Tooltip title="View"><IconButton size="small" onClick={open}><VisibilityRounded fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/notes/edit/${n.slug || n._id}`)}><EditRounded fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(n)}><DeleteRounded fontSize="small" /></IconButton></Tooltip>
      </Stack>
    );

    const chips = (
      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        {nameOf(n.project) && <Chip icon={<FolderRounded sx={{ fontSize: 14 }} />} label={nameOf(n.project)} size="small" variant="outlined" />}
        {n.entryDate && <Chip icon={<AlarmRounded sx={{ fontSize: 14 }} />} label={fmtDate(n.entryDate)} size="small" variant="outlined" />}
        {!!n.images?.length && <Chip icon={<AttachFileRounded sx={{ fontSize: 14 }} />} label={n.images.length} size="small" variant="outlined" />}
        {(n.tags || []).slice(0, 4).map((t) => (
          <Chip key={t} label={`#${t}`} size="small" sx={{ bgcolor: `${accent}1A`, color: accent, fontWeight: 700 }} />
        ))}
      </Stack>
    );

    return (
      <Box
        key={n._id}
        className="qs-animate-in"
        onClick={open}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: grid ? 'column' : 'row',
          overflow: 'hidden',
          cursor: 'pointer',
          borderRadius: '24px',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: `${accent}33`,
          boxShadow: `0 12px 28px ${accent}22`,
          animationDelay: `${Math.min(index, 12) * 0.04}s`,
          transition: 'transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease',
          '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 22px 40px ${accent}44` },
        }}
      >
        {/* glossy header / spine */}
        <Box
          sx={{
            position: 'relative', overflow: 'hidden', color: '#fff', flexShrink: 0,
            p: 2,
            width: grid ? 'auto' : { xs: 84, sm: 112 },
            minHeight: grid ? 96 : 'auto',
            display: 'flex',
            flexDirection: grid ? 'row' : 'column',
            alignItems: grid ? 'center' : 'center',
            justifyContent: grid ? 'space-between' : 'center',
            background: `linear-gradient(135deg, ${accent} 0%, ${accent}CC 55%, #F6A938 130%)`,
            '&::before': {
              content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
              background: 'linear-gradient(135deg, rgba(255,255,255,.4) 0%, rgba(255,255,255,.08) 45%, rgba(255,255,255,0) 46%, rgba(0,0,0,.12) 100%)',
            },
          }}
        >
          <CategoryIcon sx={{ position: 'absolute', right: -10, bottom: -18, fontSize: 96, color: 'rgba(255,255,255,.2)', transform: 'rotate(-12deg)' }} />
          <Box sx={{ position: 'relative' }}>{iconTile(grid ? 46 : 52)}</Box>
          {grid && (
            <Stack alignItems="flex-end" spacing={0.5} sx={{ position: 'relative' }}>
              {hasText(n.category) && (
                <Chip label={n.category} size="small" sx={{ bgcolor: 'rgba(255,255,255,.9)', color: accent, fontWeight: 800 }} />
              )}
              <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.95 }}>{fmtDate(n.updatedAt)}</Typography>
            </Stack>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Checkbox
              size="small"
              checked={bulk.isSelected(noteKey(n))}
              onClick={(e) => e.stopPropagation()}
              onChange={() => bulk.toggle(noteKey(n))}
              sx={{ p: 0.25, ml: -0.5 }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 800, flex: 1, minWidth: 0, lineHeight: 1.3 }} noWrap title={n.title}>
              {n.title || 'Untitled'}
            </Typography>
            {!grid && (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', pt: 0.5 }}>
                {fmtDate(n.updatedAt)}
              </Typography>
            )}
          </Stack>

          {!grid && hasText(n.category) && (
            <Box>
              <Chip label={n.category} size="small" sx={{ bgcolor: `${accent}1F`, color: accent, fontWeight: 800 }} />
            </Box>
          )}

          {hasText(excerpt) && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: '-webkit-box', WebkitBoxOrient: 'vertical',
                WebkitLineClamp: grid ? 3 : 2, overflow: 'hidden',
              }}
            >
              {excerpt}
            </Typography>
          )}

          <Box sx={{ flex: 1 }} />
          {chips}
          <Stack direction="row" justifyContent="flex-end">{actions}</Stack>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <PageHeader
        title="Notes"
        subtitle="Capture and organize your notes."
        actions={<Button startIcon={<AddRounded />} variant="contained" onClick={() => navigate('/notes/create')}>Add Note</Button>}
      />
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <NotesCategoryTabs
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
            totalCount={notes.length}
          />
          <TextField
            placeholder="Search notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ width: { xs: '100%', sm: 240 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded sx={{ color: 'text.secondary' }} /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Per page</InputLabel>
            <Select
              label="Per page"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              {PAGE_SIZE_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </Select>
          </FormControl>
          <ViewToggle value={cardLayout} onChange={handleSetCardLayout} />
        </Stack>
      </Box>

      <BulkSelectBar
        bulk={bulk}
        visibleIds={paged.map(noteKey)}
        noun="note"
        deleteOne={deleteNote}
        onDone={loadNotes}
      />

      {loading ? (
        <Stack spacing={2}>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={110} />)}
        </Stack>
      ) : paged.length === 0 ? (
        <EmptyState
          icon={StickyNote2Rounded}
          title="No notes yet"
          description="Try adjusting your filters or add a new note."
          actionLabel="Add Note"
          onAction={() => navigate('/notes/create')}
        />
      ) : (
        <Box
          sx={cardLayout === 'grid'
            ? { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(3, minmax(0,1fr))' }, gap: 2 }
            : { display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {paged.map((n, i) => renderCard(n, i))}
        </Box>
      )}

      {sorted.length > 0 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination count={totalPages} page={page} onChange={(_e, p) => setPage(p)} color="primary" shape="rounded" />
        </Box>
      )}

      {/* View dialog */}
      <Dialog open={!!viewNote} onClose={() => setViewNote(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{viewNote?.title || 'Note'}</DialogTitle>
        <DialogContent dividers>
          {viewNote && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                {hasText(viewNote.category) && (
                  <Chip label={viewNote.category} size="small" sx={{ bgcolor: `${categoryAccent(viewNote.category)}1F`, color: categoryAccent(viewNote.category), fontWeight: 600 }} />
                )}
                {nameOf(viewNote.project) && <Chip icon={<FolderRounded sx={{ fontSize: 14 }} />} label={nameOf(viewNote.project)} size="small" variant="outlined" />}
                {viewNote.entryDate && <Chip icon={<AlarmRounded sx={{ fontSize: 14 }} />} label={fmtDate(viewNote.entryDate)} size="small" variant="outlined" />}
              </Stack>

              {!!viewNote.tags?.length && (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                  {viewNote.tags.map((t) => <Chip key={t} label={`#${t}`} size="small" variant="outlined" />)}
                </Stack>
              )}

              {hasText(viewNote.note) && (
                <Paper variant="outlined" sx={{ p: 1.5, '& img': { maxWidth: '100%' } }}>
                  <Box className="note-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(String(viewNote.note)) }} />
                </Paper>
              )}

              {!!viewNote.images?.length && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Attachments</Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    {viewNote.images.map((f) => (
                      isImage(f) ? (
                        <Box key={f._id} component="a" href={normalizeUrl(f.url)} target="_blank" rel="noopener noreferrer" sx={{ width: 90, height: 72, overflow: 'hidden', display: 'block' }}>
                          <img src={normalizeUrl(f.url)} alt={f.filename || 'attachment'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                      ) : (
                        <Chip
                          key={f._id}
                          icon={<AttachFileRounded fontSize="small" />}
                          label={<MLink href={normalizeUrl(f.url)} underline="hover" target="_blank" rel="noopener">{f.filename || 'file'}</MLink>}
                          variant="outlined"
                        />
                      )
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewNote(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <NoteFormDialog
        open={noteDialogOpen}
        noteSlug={editingNoteSlug}
        onClose={() => navigate('/notes')}
        onSaved={() => { navigate('/notes'); loadNotes(); }}
      />
    </Box>
  );
};

export default NoteList;
